import React, { useId, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { alpha } from '@mui/material/styles';
import {
  ChartContainer,
  ChartEmptyState,
  ChartTooltip,
  InteractiveLegend,
  chartAnimation,
  useChartTheme,
} from './chartTheme';
import { colors } from '../../design-system';

const ComparisonBarChart = ({
  data = [],
  title,
  height = 320,
  xDataKey = 'name',
  leftKey = 'leftValue',
  rightKey = 'rightValue',
  leftLabel = 'Equipo A',
  rightLabel = 'Equipo B',
  showLegend = true,
  valueFormatter,
}) => {
  const chartId = useId().replace(/:/g, '-');
  const chartTheme = useChartTheme(height);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  if (!data.length) {
    return (
      <ChartContainer title={title}>
        <ChartEmptyState />
      </ChartContainer>
    );
  }

  const legendItems = [
    { dataKey: leftKey, name: leftLabel, color: colors.primary[500] },
    { dataKey: rightKey, name: rightLabel, color: colors.secondary[500] },
  ];

  const toggleSeries = (dataKey) => {
    setHiddenKeys((current) => (
      current.includes(dataKey)
        ? current.filter((key) => key !== dataKey)
        : [...current, dataKey]
    ));
  };

  return (
    <ChartContainer title={title}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={chartTheme.margin} barCategoryGap={chartTheme.isMobile ? '26%' : '30%'}>
          <defs>
            <linearGradient id={`${chartId}-left`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={alpha(colors.primary[500], 0.95)} />
              <stop offset="100%" stopColor={alpha(colors.primary[500], 0.55)} />
            </linearGradient>
            <linearGradient id={`${chartId}-right`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={alpha(colors.secondary[500], 0.95)} />
              <stop offset="100%" stopColor={alpha(colors.secondary[500], 0.55)} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke={chartTheme.palette.grid} strokeDasharray="4 4" vertical={false} />
          <XAxis
            dataKey={xDataKey}
            stroke={chartTheme.palette.axis}
            minTickGap={chartTheme.isMobile ? 24 : 16}
            interval="preserveStartEnd"
            tick={{
              fill: chartTheme.palette.textMuted,
              fontSize: chartTheme.axisFontSize,
              fontFamily: chartTheme.fontFamily,
            }}
            tickMargin={8}
          />
          <YAxis
            stroke={chartTheme.palette.axis}
            width={chartTheme.isMobile ? 32 : 44}
            tickFormatter={valueFormatter}
            tick={{
              fill: chartTheme.palette.textMuted,
              fontSize: chartTheme.axisFontSize,
              fontFamily: chartTheme.fontFamily,
            }}
          />
          <Tooltip content={<ChartTooltip valueFormatter={valueFormatter} />} />

          {!hiddenKeys.includes(leftKey) && (
            <Bar
              dataKey={leftKey}
              name={leftLabel}
              fill={`url(#${chartId}-left)`}
              radius={[chartTheme.barRadius, chartTheme.barRadius, 0, 0]}
              animationDuration={chartAnimation.duration}
              barSize={chartTheme.barSize}
            />
          )}
          {!hiddenKeys.includes(rightKey) && (
            <Bar
              dataKey={rightKey}
              name={rightLabel}
              fill={`url(#${chartId}-right)`}
              radius={[chartTheme.barRadius, chartTheme.barRadius, 0, 0]}
              animationDuration={chartAnimation.duration}
              animationBegin={chartAnimation.begin + chartAnimation.stagger}
              barSize={chartTheme.barSize}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>

      {showLegend && (
        <InteractiveLegend
          items={legendItems}
          hiddenKeys={hiddenKeys}
          onToggle={toggleSeries}
          isMobile={chartTheme.isMobile}
        />
      )}
    </ChartContainer>
  );
};

export default ComparisonBarChart;
